import type { TextVariableDto } from '@/entities/variables/model/dtos'
import type { SuggestionProps } from '@tiptap/suggestion'
import type {
  VariablesSuggestionsCommandReturn,
  VariablesSuggestionsHandle,
} from './variables.config'

import { cn } from '@/utils'
import {
  createListCollection,
  Listbox,
  useListbox,
  type ListboxValueChangeDetails,
} from '@ark-ui/react/listbox'

import { useCallback, useImperativeHandle, useMemo, useState } from 'react'

import { useRundownSnap } from '@/features/rundown/rundown-page.store'
import { useVariables } from '@/entities/variables/api/hooks'

import {
  TiptapSuggestionListItem,
  TiptapSuggestionsWidget,
} from '@/entities/tiptap-suggestions'

import { TiptapSuggestionsLoading } from '@/entities/tiptap-suggestions/ui/tiptap-suggestions-loading'
import { modalActions } from '@/features/rundown/modals/modal.store'

type VariableItem = { kind: 'variable' } & TextVariableDto

type ActionItem = {
  kind: 'action'
  key: 'action.manage' | 'action.create'
  label: string
}

type ListItemType = VariableItem | ActionItem

type VariablesSuggestionsListProps = SuggestionProps<
  string,
  VariablesSuggestionsCommandReturn
> & {
  ref: React.Ref<VariablesSuggestionsHandle>
}

export function VariablesSuggestionsList({
  query,
  command,
  ref,
}: VariablesSuggestionsListProps) {
  const { id: rundownId } = useRundownSnap()

  const { data: variables, isLoading } = useVariables({
    params: {
      rundownId,
    },
    options: {
      enabled: !!rundownId,
    },
  })

  const filteredVariables = useMemo(() => {
    if (!variables) return {}

    return Object.fromEntries(
      Object.entries(variables)
        .filter(([key, value]) => {
          const searchText = [key, value]
            .map((n) => n.trim().toLowerCase())
            .join('')
          return searchText.includes(query.toLowerCase())
        })
        .sort(([a], [b]) =>
          a.toLowerCase().localeCompare(b.toLowerCase(), 'en'),
        ),
    )
  }, [variables, query])

  const items: ListItemType[] = useMemo(() => {
    const variablesEntries = Object.entries(filteredVariables)
    const hasMentions = variablesEntries.length > 0

    if (hasMentions) {
      return [
        ...variablesEntries.map(([key, value]) => {
          return {
            kind: 'variable' as const,
            key,
            value,
          }
        }),
        {
          kind: 'action',
          key: 'action.manage',
          label: 'Manage $text-variables',
        },
      ]
    }

    return [
      { kind: 'action', key: 'action.create', label: 'Create $text-variables' },
    ]
  }, [filteredVariables])

  const collection = createListCollection<ListItemType>({
    items,
    itemToValue: (item) => item.key,
    itemToString: (item) => item.key,
  })

  const handleValueChange = useCallback(
    ({ value: newIds }: ListboxValueChangeDetails<ListItemType>) => {
      const selected = collection.find(newIds[0])

      if (!selected) return

      if (selected.kind === 'variable') {
        command({ variable: selected })
      } else if (selected.kind === 'action') {
        // Open modal for manage/create
        modalActions.open('VARIABLES', {})
      }
    },
    [collection, command],
  )

  const [highlighted, setHighlighted] = useState<string | null>(
    () => (items[0]?.key as string | undefined) ?? null,
  )
  const listbox = useListbox({
    collection: collection,
    onValueChange: handleValueChange,
    highlightedValue: highlighted,
    onHighlightChange: ({ highlightedValue }) =>
      setHighlighted(highlightedValue),
    selectionMode: 'single',
    loopFocus: true,
    orientation: 'vertical',
    deselectable: true,
  })

  const handleUp = useCallback(() => {
    const { firstValue, lastValue, getPreviousValue } = listbox.collection

    setHighlighted((prev) => {
      if (prev === firstValue) return lastValue

      if (prev) {
        return getPreviousValue(prev) ?? null
      }

      return firstValue
    })
  }, [listbox.collection])

  const handleDown = useCallback(() => {
    const { firstValue, lastValue, getNextValue } = listbox.collection

    setHighlighted((prev) => {
      if (prev === lastValue) return firstValue

      if (prev) {
        return getNextValue(prev) ?? null
      }

      return firstValue
    })
  }, [listbox.collection])

  const handleEnter = useCallback(() => {
    if (!highlighted) return

    const selected = collection.find(highlighted)
    if (!selected) return

    if (selected.kind === 'variable') {
      command({ variable: selected })
    } else if (selected.kind === 'action') {
      modalActions.open('VARIABLES', {})
    }
  }, [collection, highlighted, command])

  useImperativeHandle(ref, () => ({
    onKeyDown({ event }) {
      switch (event.key) {
        case 'ArrowUp': {
          handleUp()
          return true
        }
        case 'ArrowDown': {
          handleDown()
          return true
        }
        case 'Enter': {
          handleEnter()
          return true
        }
      }

      // Don't handle unknown keys
      return false
    },
  }))

  if (isLoading) {
    return <TiptapSuggestionsLoading text="Loading variables..." />
  }

  return (
    <>
      <TiptapSuggestionsWidget>
        <Listbox.RootProvider value={listbox}>
          <Listbox.Content
            {...listbox.getContentProps()}
            className="flex flex-col gap-1 divide-y divide-gray-600"
          >
            {collection.items.map((item, index) => {
              const isHighlighted = highlighted === item.key
              const itemProps = {
                item,
                highlightOnHover: true,
              }

              if (item.kind === 'variable') {
                const { key } = item

                const value = variables?.[key]

                return (
                  <TiptapSuggestionListItem
                    {...listbox.getItemProps(itemProps)}
                    key={key}
                    item={item}
                    className={cn(
                      'mx-0.75 rounded-sm border-0',
                      index === 0 && 'mt-0.75',
                      isHighlighted && 'ring-3',
                    )}
                  >
                    <Listbox.ItemText
                      {...listbox.getItemTextProps(itemProps)}
                      className="w-min whitespace-pre flcamex rounded-sm"
                    >
                      <div className="flex flex-col px-1">
                        <span className="leading-tight text-sm">{value}</span>
                        <span className="text-xs opacity-70 leading-none">
                          {key}
                        </span>
                      </div>
                    </Listbox.ItemText>
                  </TiptapSuggestionListItem>
                )
              }

              return (
                <TiptapSuggestionListItem
                  {...listbox.getItemProps(itemProps)}
                  key={item.key}
                  item={item}
                  className={cn(
                    'bg-gray-700',
                    isHighlighted && 'ring-inset ring-3',
                  )}
                >
                  <Listbox.ItemText
                    {...listbox.getItemTextProps(itemProps)}
                    className="text-sm"
                  >
                    {item.label}
                  </Listbox.ItemText>
                </TiptapSuggestionListItem>
              )
            })}
          </Listbox.Content>
        </Listbox.RootProvider>
      </TiptapSuggestionsWidget>
    </>
  )
}
